// File: src/hooks/useAssignments.ts

import { useState, useCallback } from 'react';
import { Assignment, CreateAssignmentRequest, AssignmentFormData, ReturnFormData } from '../types/assignment';
import { InventoryItem as APIInventoryItem } from '../services/inventoryAPI';
import { assignmentAPI } from '../services/assignmentAPI';
import { inventoryAPI } from '../services/inventoryAPI';

interface UseAssignmentsReturn {
  assignments: Assignment[];
  loading: boolean;
  error: string | null;
  
  // CRUD operations
  createAssignment: (data: CreateAssignmentRequest) => Promise<Assignment>;
  updateAssignment: (id: string, data: any) => Promise<Assignment>;
  deleteAssignment: (id: string) => Promise<void>;
  
  // Specialized queries
  getOfficerEquipment: (userId: string) => Promise<Assignment[]>;
  getItemHistory: (itemId: string) => Promise<Assignment[]>;
  
  // UI helpers
  assignItem: (item: APIInventoryItem, assignmentData: AssignmentFormData) => Promise<void>;
  returnItem: (assignment: Assignment, returnData: ReturnFormData) => Promise<void>;
  
  // State management
  refreshAssignments: () => Promise<void>;
  clearError: () => void;
}

export const useAssignments = (): UseAssignmentsReturn => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleError = useCallback((err: any) => {
    const message = err?.response?.data?.message || err?.message || 'An unexpected error occurred';
    setError(message);
    console.error('Assignment operation error:', err);
  }, []);

  const refreshAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await assignmentAPI.getAssignmentsCompat();
      setAssignments(response);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const createAssignment = useCallback(async (data: CreateAssignmentRequest): Promise<Assignment> => {
    try {
      setLoading(true);
      setError(null);
      const response = await assignmentAPI.createAssignment(data);
      if (response.success && response.data) {
        setAssignments(prev => [...prev, response.data!]);
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Failed to create assignment');
      }
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const updateAssignment = useCallback(async (id: string, data: any): Promise<Assignment> => {
    try {
      setLoading(true);
      setError(null);
      const response = await assignmentAPI.updateAssignment(id, data);
      if (response.success && response.data) {
        setAssignments(prev => prev.map(a => a.assignmentId === id ? response.data! : a));
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Failed to update assignment');
      }
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const deleteAssignment = useCallback(async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await assignmentAPI.returnAssignment(id, { condition: 'Good', notes: '' });
      setAssignments(prev => prev.filter(a => a.assignmentId !== id));
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const getOfficerEquipment = useCallback(async (userId: string): Promise<Assignment[]> => {
    try {
      setError(null);
      return await assignmentAPI.getOfficerEquipmentCompat(userId);
    } catch (err) {
      handleError(err);
      return [];
    }
  }, [handleError]);

  const getItemHistory = useCallback(async (itemId: string): Promise<Assignment[]> => {
    try {
      setError(null);
      return await assignmentAPI.getItemHistoryCompat(itemId);
    } catch (err) {
      handleError(err);
      return [];
    }
  }, [handleError]);

  const assignItem = useCallback(async (item: APIInventoryItem, assignmentData: AssignmentFormData): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Create assignment request using your actual item structure
      const createRequest: CreateAssignmentRequest = {
        assignment: {
          inventoryItemId: item.itemId,
          assignedToUserId: assignmentData.assignedToUserId,
          assignmentReason: assignmentData.assignmentReason,
          notes: assignmentData.notes
        }
      };

      // Create assignment record
      await createAssignment(createRequest);

      // Update inventory item status/quantity based on item type
      if (item.itemType === 'SERIALIZED') {
        // Serialized item - mark as assigned
        const response = await inventoryAPI.updateItem(item.itemId, {
          status: 'Assigned'
        });
        
        if (!response.success) {
          throw new Error(response.error?.message || 'Failed to update inventory item');
        }
     } else {
        // Bulk item - this would need quantity management
        // For now, just log that it needs implementation
        console.log('Bulk item assignment - inventory quantity update needed');
      }

    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [createAssignment, handleError]);

  const returnItem = useCallback(async (assignment: Assignment, returnData: ReturnFormData): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Process return through assignment API
      await assignmentAPI.returnAssignment(assignment.assignmentId, returnData);

      // Update local state
      setAssignments(prev => prev.filter(a => a.assignmentId !== assignment.assignmentId));

      // Update inventory item status
      if (assignment.itemSerialNumber) {
        // Serialized item - update status based on condition
        let newStatus = 'Available';
        if (returnData.condition === 'Damaged') {
          newStatus = 'Needs Repair';
        } else if (returnData.condition === 'Lost') {
          newStatus = 'Lost';
        }

        await inventoryAPI.updateItem(assignment.inventoryItemId, {
          status: newStatus as any
        });
      }

    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  return {
    assignments,
    loading,
    error,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    getOfficerEquipment,
    getItemHistory,
    assignItem,
    returnItem,
    refreshAssignments,
    clearError
  };
};